import { useState, useEffect, useRef, useCallback } from 'react';
import { stratifyPopulation, drawSample, fmt$, fmtPct,
         applyTossStratum, computeProjectedRP,
         computeAttributeSampleSize, getSamplingModeRecommendation } from '../lib/samplingEngine';
import { deductCredit, getCredits, submitForApproval, resetApproval } from '../lib/supabase';

// ── Color tokens ──────────────────────────────────────────────────────────────
const NAVY = '#0D2461', AMBER = '#F5A800', CREAM = '#f4f6fb', BORDER = '#c8d0e0';
const STRATA_COLORS = ['#0D2461','#c98a00','#6040a0','#1a5c3a','#a03030',
                       '#1a5080','#8a5a10','#3a5020'];

export default function SamplePanel({ project, population, existingSample, onDraw, saving, profile, onProjectUpdate }) {
  // ── Sizing state ──
  const [sizeMode,    setSizeMode]    = useState('fixed');
  const [fixedN,      setFixedN]      = useState(5);
  const [perStratumN, setPerStratumN] = useState({});
  const [targetRP,    setTargetRP]    = useState(0.15);
  const [top100N,     setTop100N]     = useState(5);
  const [numStrata,   setNumStrata]   = useState(5);

  // ── Toss stratum state ──
  const [tossEnabled, setTossEnabled] = useState(false);
  const [tossPct,     setTossPct]     = useState(2);

  // ── Sampling mode ──
  const [samplingMode,    setSamplingMode]    = useState('variable'); // 'variable' | 'attribute'
  const [attrConfidence,  setAttrConfidence]  = useState(0.95);
  const [attrTolerable,   setAttrTolerable]   = useState(5);   // percent
  const [attrExpected,    setAttrExpected]    = useState(0);    // percent

  // ── Custom boundaries from slider ──
  const [customBoundaries, setCustomBoundaries] = useState(null);

  // ── Computed ──
  const [strata,      setStrata]      = useState([]);
  const [large100,    setLarge100]    = useState([]);
  const [tossed,      setTossed]      = useState([]);
  const [samplingPool,setSamplingPool]= useState([]);
  const [projectedRP, setProjectedRP] = useState(null);
  const [credits,     setCredits]     = useState(null);
  const [drawing,     setDrawing]     = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  const teamId = profile?.team_id;

  // ── Approval dirty-flag: fires resetApproval once per approval cycle on any setting change ──
  const approvalDirtyRef = useRef(false);

  // Reset the flag whenever the approval status changes (re-approved, re-submitted, etc.)
  useEffect(() => {
    approvalDirtyRef.current = false;
  }, [project?.approval_status]); // eslint-disable-line

  const maybeResetApproval = useCallback(async () => {
    if (project?.approval_status !== 'approved') return;
    if (approvalDirtyRef.current) return;
    approvalDirtyRef.current = true;
    const { data } = await resetApproval(project.id);
    if (data && onProjectUpdate) onProjectUpdate(data);
  }, [project?.approval_status, project?.id, onProjectUpdate]); // eslint-disable-line

  useEffect(() => {
    if (!population) return;

    // Priority: draw settings (post-draw) > plan_settings (saved at submit) > defaults
    const s = existingSample?.settings || project?.plan_settings || null;

    if (s) {
      const sm    = s.sizeMode     ?? 'fixed';
      const fN    = s.fixedN       ?? 5;
      const psN   = s.perStratumN  ?? {};
      const rp    = s.targetRP     ?? 0.15;
      const t100N = s.top100N      ?? 5;
      const nStr  = s.numStrata    ?? 5;
      const tOn   = s.tossEnabled  ?? false;
      const tPct  = s.tossPct      ?? 2;
      const cb    = s.customBoundaries ?? null;
      setSizeMode(sm);
      setFixedN(fN);
      setPerStratumN(psN);
      setTargetRP(rp);
      setTop100N(t100N);
      setNumStrata(nStr);
      setTossEnabled(tOn);
      setTossPct(tPct);
      setCustomBoundaries(cb);
      if (s.samplingMode   !== undefined) setSamplingMode(s.samplingMode);
      if (s.attrConfidence !== undefined) setAttrConfidence(s.attrConfidence);
      if (s.attrTolerable  !== undefined) setAttrTolerable(s.attrTolerable);
      if (s.attrExpected   !== undefined) setAttrExpected(s.attrExpected);
      recompute(sm, rp, fN, psN, t100N, nStr, cb, tOn, tPct);
    } else {
      recompute(sizeMode, targetRP, fixedN, perStratumN, top100N, numStrata, customBoundaries, tossEnabled, tossPct);
    }

    if (teamId) getCredits(teamId).then(({ credits: c }) => setCredits(c));
  }, [population, existingSample?.id, project?.id]); // eslint-disable-line

  const recompute = (mode, rp, fN, psN, t100N, nStr, bounds, tossOn, tPct) => {
    if (!population) return;
    const { samplingPool: pool, tossed: toss } = applyTossStratum(population, { tossEnabled: tossOn, tossPct: tPct });
    setSamplingPool(pool);
    setTossed(toss || []);
    const { strata: st, large100: l100 } = stratifyPopulation(pool, {
      sizeMode: mode, targetRP: rp, fixedN: fN, perStratumN: psN,
      top100N: t100N, numStrata: nStr, boundaries: bounds,
    });
    setStrata(st);
    setLarge100(l100);
    const proj = computeProjectedRP(st, l100);
    setProjectedRP(proj);
    setCustomBoundaries(bounds);
  };

  const handleMode        = (m)    => { maybeResetApproval(); setSizeMode(m);    recompute(m, targetRP, fixedN, perStratumN, top100N, numStrata, customBoundaries, tossEnabled, tossPct); };
  const handleFixedN      = (v)    => { maybeResetApproval(); const n=Math.max(1,parseInt(v)||1); setFixedN(n); recompute(sizeMode, targetRP, n, perStratumN, top100N, numStrata, customBoundaries, tossEnabled, tossPct); };
  const handleRP          = (rp)   => { maybeResetApproval(); setTargetRP(rp);   recompute(sizeMode, rp, fixedN, perStratumN, top100N, numStrata, customBoundaries, tossEnabled, tossPct); };
  const handleTop100      = (v)    => { maybeResetApproval(); const n=Math.max(0,parseInt(v)||0); setTop100N(n); recompute(sizeMode, targetRP, fixedN, perStratumN, n, numStrata, customBoundaries, tossEnabled, tossPct); };
  const handleNumStr      = (n)    => { maybeResetApproval(); setNumStrata(n);   setCustomBoundaries(null); recompute(sizeMode, targetRP, fixedN, perStratumN, top100N, n, null, tossEnabled, tossPct); };
  const handlePerStr      = (id,v) => { maybeResetApproval(); const u={...perStratumN,[id]:v}; setPerStratumN(u); recompute(sizeMode, targetRP, fixedN, u, top100N, numStrata, customBoundaries, tossEnabled, tossPct); };
  const handleToss        = (on)   => { maybeResetApproval(); setTossEnabled(on); recompute(sizeMode, targetRP, fixedN, perStratumN, top100N, numStrata, customBoundaries, on, tossPct); };
  const handleTossPct     = (v)    => { maybeResetApproval(); const p=Math.min(Math.max(parseFloat(v)||2,0.1),20); setTossPct(p); recompute(sizeMode, targetRP, fixedN, perStratumN, top100N, numStrata, customBoundaries, tossEnabled, p); };
  const handleBounds      = (b)    => { maybeResetApproval(); setCustomBoundaries(b); recompute(sizeMode, targetRP, fixedN, perStratumN, top100N, numStrata, b, tossEnabled, tossPct); };
  const handleSamplingMode     = (m) => { maybeResetApproval(); setSamplingMode(m); };
  const handleAttrConfidence   = (v) => { maybeResetApproval(); setAttrConfidence(v); };
  const handleAttrTolerable    = (v) => { maybeResetApproval(); setAttrTolerable(parseFloat(v) || 5); };
  const handleAttrExpected     = (v) => { maybeResetApproval(); setAttrExpected(parseFloat(v) || 0); };

  const totalSampleN = strata.reduce((s, st) => s + st.n, 0) + large100.length;
  const modeRec = population ? getSamplingModeRecommendation(population.length, totalSampleN) : null;
  const attrPlan = population ? computeAttributeSampleSize(
    (samplingPool || population).length,
    { confidenceLevel: attrConfidence, tolerableRate: attrTolerable / 100, expectedRate: attrExpected / 100 }
  ) : null;
  const totalPopAmt  = population ? population.reduce((s, p) => s + p.amount, 0) : 0;
  const smallSample  = totalSampleN < 30;

  const handleSubmitApproval = async () => {
    setSubmitting(true);
    const planSettings = {
      sizeMode, fixedN, targetRP, top100N, numStrata, perStratumN,
      tossEnabled, tossPct, customBoundaries, samplingMode,
      attrConfidence, attrTolerable, attrExpected,
    };
    const { data } = await submitForApproval(project.id, planSettings);
    if (data && onProjectUpdate) onProjectUpdate(data);
    setSubmitting(false);
  };

  const handleDraw = async () => {
    setError('');
    if (credits !== null && credits < 1) {
      setError('Insufficient credits. Purchase credits from the dashboard.');
      return;
    }
    setDrawing(true);
    const { error: creditErr } = await deductCredit(teamId);
    if (creditErr) { setError('Could not deduct credit: ' + creditErr.message); setDrawing(false); return; }

    const seed = Date.now() % 99999;
    const drawn = drawSample(strata, large100, seed);
    const strataWithNi = strata.map(st => ({
      ...st, Ni: st.projects.length, projectCount: st.projects.length,
      stratumTotal: st.projects.reduce((s, p) => s + p.amount, 0),
      projects: [],
    }));

    await onDraw({
      seed, strata: strataWithNi, large100: large100.map(p => ({ ...p })),
      drawnProjects: drawn, tossed,
      projectedRP: projectedRP?.relPrecision || null,
      samplingMode,
      attrPlan: samplingMode === 'attribute' ? attrPlan : null,
      settings: { sizeMode, fixedN, targetRP, top100N, numStrata, perStratumN,
                  tossEnabled, tossPct, customBoundaries, samplingMode,
                  attrConfidence, attrTolerable, attrExpected },
    });
    setDrawing(false);
  };

  if (!population) return (
    <div style={card}><div style={eyebrow}>Step 2 of 5</div><h2 style={title}>Sampling Plan</h2>
      <div style={placeholder}>Upload your project population first.</div></div>
  );

  const rp = projectedRP?.relPrecision;
  const rpColor = rp == null ? '#8a96b0' : rp <= 0.10 ? '#1a5c3a' : rp <= 0.15 ? '#c98a00' : '#c03a1a';
  const rpBg    = rp == null ? CREAM : rp <= 0.10 ? '#e4f2eb' : rp <= 0.15 ? '#fff8e6' : '#fff0ee';
  const rpLabel = rp == null ? '—' : rp <= 0.10 ? '✓ Point Estimate Permitted' : rp <= 0.15 ? '⚠ Interpolation Zone' : '✗ Lower Limit Required';

  return (
    <div style={card}>
      <div style={eyebrow}>Step 2 of 5</div>
      <h2 style={title}>Sampling Plan &amp; Draw</h2>
      <p style={sub}>Configure stratification and sample size. The projected precision assumes 100% qualification.</p>

      {/* ── Credit indicator ── */}
      {credits !== null && (
        <div style={{ ...callout, borderColor: credits > 0 ? NAVY : '#c03a1a',
          background: credits > 0 ? '#e8ecf5' : '#fff0ee',
          color: credits > 0 ? NAVY : '#c03a1a', marginBottom: 24 }}>
          {credits > 0
            ? `✓ ${credits} credit${credits !== 1 ? 's' : ''} available — 1 will be deducted when you draw.`
            : '✗ No credits remaining. Purchase credits from the dashboard.'}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SAMPLING MODE SELECTOR
      ══════════════════════════════════════════════════════ */}

      {/* Mode recommendation banner */}
      {modeRec && modeRec.recommended !== 'variable' && (
        <div style={{
          ...callout,
          borderColor: modeRec.recommended === 'attribute' ? '#c98a00' : '#1a5080',
          background: modeRec.recommended === 'attribute' ? '#fff8e6' : '#e0f0f8',
          color: modeRec.recommended === 'attribute' ? '#7a5c10' : '#1a5080',
          marginBottom: 20,
        }}>
          {modeRec.recommended === 'attribute' ? '⚠' : 'ℹ'} <strong>Sampling Mode Recommendation:</strong> {modeRec.reason}
        </div>
      )}

      {/* Mode toggle */}
      <div style={controlBox}>
        <div style={controlTitle}>Sampling Mode</div>
        <div style={modeTabs}>
          <button style={{...modeTab, ...(samplingMode==='variable' ? modeTabActive : {})}}
            onClick={() => handleSamplingMode('variable')}>
            Variable Sampling (Rev. Proc. 2011-42 §4.03)
          </button>
          <button style={{...modeTab, ...(samplingMode==='attribute' ? modeTabActive : {})}}
            onClick={() => handleSamplingMode('attribute')}>
            Attribute Sampling (Rev. Proc. 2011-42 §4.05)
          </button>
        </div>
        {samplingMode === 'variable' && (
          <div style={hint}>
            Variable sampling estimates a dollar amount (QRE). Requires minimum 30 projects per stratum for valid statistical inference.
            Best for populations ≥ 500 with sufficient sample size.
          </div>
        )}
        {samplingMode === 'attribute' && (
          <div style={hint}>
            Attribute sampling tests whether an error rate exceeds a tolerable threshold.
            More efficient for small populations or when the question is binary (qualifies / does not qualify).
          </div>
        )}
      </div>

      {/* ── Attribute sampling controls ── */}
      {samplingMode === 'attribute' && (
        <div style={controlBox}>
          <div style={controlTitle}>Attribute Sampling Plan — Rev. Proc. 2011-42 §4.05</div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, marginBottom:20}}>
            <div style={fieldGroup}>
              <label style={label}>Confidence Level</label>
              <div style={{display:'flex', gap:8}}>
                {[[0.95,'95%'],[0.90,'90%']].map(([v,lbl]) => (
                  <button key={v} style={{...pill, ...(attrConfidence===v ? pillActive : {})}}
                    onClick={() => handleAttrConfidence(v)}>{lbl}</button>
                ))}
              </div>
              <div style={{...hint,marginTop:6}}>One-sided upper limit</div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Tolerable Error Rate (%)</label>
              <div style={inlineRow}>
                <input style={{...numInput,width:70}} type="number" min="1" max="50" step="0.5"
                  value={attrTolerable} onChange={e => handleAttrTolerable(e.target.value)} />
                <span style={hint}>% of population</span>
              </div>
              <div style={{...hint,marginTop:4}}>Maximum acceptable error rate</div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>Expected Error Rate (%)</label>
              <div style={inlineRow}>
                <input style={{...numInput,width:70}} type="number" min="0" max="49" step="0.5"
                  value={attrExpected} onChange={e => handleAttrExpected(e.target.value)} />
                <span style={hint}>% expected</span>
              </div>
              <div style={{...hint,marginTop:4}}>Best estimate of actual rate (0 = conservative)</div>
            </div>
          </div>

          {attrPlan && (
            <>
              <div style={statRow}>
                <div style={stat}>
                  <div style={statLabel}>Required Sample Size</div>
                  <div style={{...statVal, color:'#c98a00'}}>{attrPlan.n}</div>
                </div>
                <div style={stat}>
                  <div style={statLabel}>Infinite Pop. n₀</div>
                  <div style={{...statVal, fontSize:18}}>{attrPlan.n0}</div>
                </div>
                <div style={stat}>
                  <div style={statLabel}>FP Correction Applied</div>
                  <div style={{...statVal, fontSize:16}}>{attrPlan.fpCorrection ? 'Yes' : 'No'}</div>
                </div>
              </div>
              <div style={{...callout, marginTop:8}}>
                At <strong>{(attrConfidence*100).toFixed(0)}% confidence</strong>, a sample of{' '}
                <strong>{attrPlan.n} projects</strong> from a population of {attrPlan.N} is required
                to conclude the error rate does not exceed <strong>{attrTolerable}%</strong>,
                assuming an expected rate of {attrExpected}%.
                {attrPlan.fpCorrection && ` (Reduced from ${attrPlan.n0} using finite population correction.)`}
              </div>
            </>
          )}

          <button style={{...primaryBtn, marginTop:20,
            opacity:(drawing||saving||(credits!==null&&credits<1))?0.5:1}}
            onClick={handleDraw}
            disabled={drawing||saving||(credits!==null&&credits<1)}>
            {drawing ? '🎲 Drawing…' : saving ? 'Saving…' : `🎲 Draw Attribute Sample (n=${attrPlan?.n || '—'}) →`}
          </button>
        </div>
      )}

      {/* Variable sampling sections — hidden in attribute mode */}
      {samplingMode === 'variable' && (<>

      {/* ══════════════════════════════════════════════════════
          SECTION 1 — CERTAINTY & TOSS STRATA
      ══════════════════════════════════════════════════════ */}
      <div style={controlBox}>
        <div style={controlTitle}>Certainty &amp; Toss Strata</div>
        <div style={twoCol}>
          {/* Certainty stratum */}
          <div style={fieldGroup}>
            <label style={label}>Certainty Stratum — Top Projects (100% Tested)</label>
            <div style={inlineRow}>
              <input style={{...numInput, width:70}} type="number" min="0" max={population.length}
                value={top100N} onChange={e => handleTop100(e.target.value)} />
              <span style={hint}>top projects by $ · always fully reviewed</span>
            </div>
            {large100.length > 0 && (
              <div style={{...hint, marginTop:6}}>
                <strong style={{color:NAVY}}>{large100.length} projects</strong> · {fmt$(large100.reduce((s,p)=>s+p.amount,0))} · threshold ≥ {fmt$(large100[large100.length-1]?.amount)}
              </div>
            )}
          </div>

          {/* Toss stratum */}
          <div style={fieldGroup}>
            <label style={label}>Toss Stratum — Exclude Smallest QREs</label>
            <div style={inlineRow}>
              <button style={{...toggleBtn, background: tossEnabled ? NAVY : 'white',
                color: tossEnabled ? AMBER : '#8a96b0', borderColor: tossEnabled ? NAVY : BORDER}}
                onClick={() => handleToss(!tossEnabled)}>
                {tossEnabled ? 'On' : 'Off'}
              </button>
              {tossEnabled && (
                <>
                  <input style={{...numInput, width:60}} type="number" min="0.1" max="20" step="0.1"
                    value={tossPct} onChange={e => handleTossPct(e.target.value)} />
                  <span style={hint}>% of total QRE value</span>
                </>
              )}
            </div>
            {tossEnabled && tossed.length > 0 && (
              <div style={{...hint, marginTop:6, color:'#c03a1a'}}>
                <strong>{tossed.length} projects tossed</strong> · {fmt$(tossed.reduce((s,p)=>s+p.amount,0))} excluded from sampling
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          SECTION 2 — VISUAL STRATA SLIDER
      ══════════════════════════════════════════════════════ */}
      <div style={controlBox}>
        <div style={controlTitle}>Strata Boundaries — Population Distribution</div>
        <div style={{...twoCol, marginBottom: 12}}>
          <div style={fieldGroup}>
            <label style={label}>Number of Sampled Strata</label>
            <div style={{display:'flex', gap:6}}>
              {[2,3,4,5,6,7,8].map(n => (
                <button key={n} style={{width:34,height:34,border:'1.5px solid',
                  borderColor: numStrata===n ? NAVY : BORDER, borderRadius:4,
                  background: numStrata===n ? NAVY : 'white',
                  color: numStrata===n ? AMBER : '#8a96b0',
                  fontFamily:"'Roboto Mono',monospace", fontSize:13, fontWeight:700, cursor:'pointer'}}
                  onClick={() => handleNumStr(n)}>{n}</button>
              ))}
            </div>
          </div>
          <div style={fieldGroup}>
            <label style={label}>Projected Relative Precision (100% qual)</label>
            <div style={{background: rpBg, border:`1.5px solid ${rpColor}`, borderRadius:6,
              padding:'10px 14px', fontFamily:"'Roboto Mono',monospace", fontSize:12,
              color: rpColor, lineHeight:1.5}}>
              <strong style={{fontSize:18}}>{rp != null ? fmtPct(rp) : '—'}</strong><br/>
              {rpLabel}
            </div>
          </div>
        </div>

        <StrataSlider
          population={samplingPool}
          large100={large100}
          strata={strata}
          numStrata={numStrata}
          onBoundariesChange={handleBounds}
        />
      </div>

      {/* ══════════════════════════════════════════════════════
          SECTION 3 — SAMPLE SIZE METHOD
      ══════════════════════════════════════════════════════ */}
      <div style={controlBox}>
        <div style={controlTitle}>Sample Size Method</div>
        <div style={modeTabs}>
          {[['fixed','Fixed per Stratum'],['perStratum','Per-Stratum Override'],['formula','Precision Formula']].map(([m,lbl]) => (
            <button key={m} style={{...modeTab, ...(sizeMode===m ? modeTabActive : {})}} onClick={() => handleMode(m)}>{lbl}</button>
          ))}
        </div>

        {sizeMode === 'fixed' && (
          <div style={twoCol}>
            <div style={fieldGroup}>
              <label style={label}>Projects per Sampled Stratum</label>
              <div style={inlineRow}>
                <input style={{...numInput, width:80}} type="number" min="1" max="50"
                  value={fixedN} onChange={e => handleFixedN(e.target.value)} />
                <span style={hint}>per stratum · default: 5</span>
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>About this method</label>
              <div style={hint}>Equal sample per stratum. Minimizes interview burden. Actual precision computed after scoring.</div>
            </div>
          </div>
        )}

        {sizeMode === 'perStratum' && (
          <div>
            <div style={{...hint, marginBottom:12}}>Set a specific n for each stratum. Leave blank to use default ({fixedN}).</div>
            {strata.map(st => (
              <div key={st.id} style={{display:'grid', gridTemplateColumns:'1fr 80px 120px', gap:12,
                alignItems:'center', padding:'10px 0', borderBottom:`1px solid #f0f2f8`}}>
                <div>
                  <div style={{fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:13, color:NAVY}}>{st.label}</div>
                  <div style={hint}>{st.projects.length} projects · {fmt$(st.projects.reduce((s,p)=>s+p.amount,0))}</div>
                </div>
                <input style={numInput} type="number" min="1" max={st.projects.length}
                  placeholder={fixedN} value={perStratumN[st.id] ?? ''}
                  onChange={e => handlePerStr(st.id, e.target.value)} />
                <div style={hint}>n={st.n} ({fmtPct(st.n/st.projects.length)})</div>
              </div>
            ))}
          </div>
        )}

        {sizeMode === 'formula' && (
          <div style={twoCol}>
            <div style={fieldGroup}>
              <label style={label}>Target Relative Precision</label>
              <div style={{display:'flex', gap:8}}>
                {[[0.10,'10%'],[0.15,'15%'],[0.20,'20%']].map(([v,lbl]) => (
                  <button key={v} style={{...pill, ...(targetRP===v ? pillActive : {})}} onClick={() => handleRP(v)}>{lbl}</button>
                ))}
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={label}>About this method</label>
              <div style={hint}>Formula-driven n per stratum to hit your precision target.</div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          SECTION 4 — STRATA SUMMARY TABLE
      ══════════════════════════════════════════════════════ */}
      <div style={{marginBottom:20}}>
        <div style={statRow}>
          <div style={stat}><div style={statLabel}>Population (N)</div>
            <div style={statVal}>{(samplingPool||population).length}
              {tossEnabled && tossed.length > 0 && <span style={{fontSize:11,color:'#c03a1a',marginLeft:6}}>(-{tossed.length} tossed)</span>}
            </div>
          </div>
          <div style={stat}><div style={statLabel}>Total Pop ($)</div>
            <div style={{...statVal,fontSize:15}}>{fmt$(totalPopAmt)}</div>
          </div>
          <div style={stat}><div style={statLabel}>Sample Size (n)</div>
            <div style={{...statVal,color: smallSample ? '#c03a1a' : '#c98a00'}}>{totalSampleN}</div>
          </div>
        </div>

        {smallSample && (
          <div style={{...callout, borderColor:'#c03a1a', background:'#fff0ee', color:'#c03a1a', marginBottom:16}}>
            ⚠ <strong>Small sample warning:</strong> Total sample of {totalSampleN} projects is below the recommended minimum of 30.
            The t-distribution will apply and precision will be reduced. Consider increasing the number of strata or projects per stratum.
            This limitation will be documented in the sampling plan section of the report.
          </div>
        )}

        <table style={table}>
          <thead><tr>
            {['Stratum','Definition','Population','Total Amount','Sample (n)','Rate'].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {large100.length > 0 && (
              <tr style={{borderBottom:'1px solid #f0f2f8'}}>
                <td style={td}><span style={{...strataBadge,background:'#fff0ee',color:'#c03a1a'}}>100%</span></td>
                <td style={td}>Certainty (Top {top100N})</td>
                <td style={td}>{large100.length}</td>
                <td style={{...td,fontWeight:600,color:NAVY}}>{fmt$(large100.reduce((s,p)=>s+p.amount,0))}</td>
                <td style={{...td,fontWeight:700,color:'#c98a00'}}>{large100.length}</td>
                <td style={td}>100%</td>
              </tr>
            )}
            {strata.map((st,i) => (
              <tr key={st.id} style={{borderBottom:'1px solid #f0f2f8'}}>
                <td style={td}><span style={{...strataBadge,background:STRATA_COLORS[i]+'22',color:STRATA_COLORS[i]}}>{st.id}</span></td>
                <td style={td}>{st.label}</td>
                <td style={td}>{st.projects.length}</td>
                <td style={{...td,fontWeight:600,color:NAVY}}>{fmt$(st.projects.reduce((s,p)=>s+p.amount,0))}</td>
                <td style={{...td,fontWeight:700,color:'#c98a00'}}>{st.n}
                  {st.n < 5 && <span style={{color:'#c03a1a',marginLeft:4,fontSize:10}}>⚠</span>}
                </td>
                <td style={td}>{fmtPct(st.n/st.projects.length)}</td>
              </tr>
            ))}
            {tossEnabled && tossed.length > 0 && (
              <tr style={{borderBottom:'1px solid #f0f2f8',opacity:0.5}}>
                <td style={td}><span style={{...strataBadge,background:'#f0f2f8',color:'#8a96b0'}}>TOSS</span></td>
                <td style={td}>Tossed (bottom {tossPct}% by $)</td>
                <td style={td}>{tossed.length}</td>
                <td style={{...td,color:'#8a96b0'}}>{fmt$(tossed.reduce((s,p)=>s+p.amount,0))}</td>
                <td style={{...td,color:'#8a96b0'}}>0</td>
                <td style={{...td,color:'#8a96b0'}}>—</td>
              </tr>
            )}
            <tr>
              <td colSpan={2} style={{...td,fontWeight:700}}>Total</td>
              <td style={{...td,fontWeight:700}}>{population.length}</td>
              <td style={{...td,fontWeight:700,color:NAVY}}>{fmt$(totalPopAmt)}</td>
              <td style={{...td,fontWeight:700,color:'#c98a00'}}>{totalSampleN}</td>
              <td style={{...td,fontWeight:700}}>{fmtPct(totalSampleN/population.length)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {error && <div style={errorBox}>{error}</div>}
      <div style={{...callout, marginBottom:24}}>
        <strong>Rev. Proc. 2011-42:</strong> Random seed recorded for Appendix B. Sample saved to database —
        close and return at any time. Each draw is logged for the final report.
      </div>

      {/* Approval gate */}
      {project?.approval_status === 'rejected' && (
        <div style={{...callout, borderColor:'#c03a1a', background:'#fff0ee', color:'#c03a1a', marginBottom:16}}>
          ✗ <strong>Sample plan rejected.</strong>{project.approval_note ? ` Reason: ${project.approval_note}` : ''}
          {' '}Revise the plan and resubmit for approval.
        </div>
      )}

      {(!project?.approval_status || project?.approval_status === 'pending' || project?.approval_status === 'rejected') && (
        <button style={{...primaryBtn, background:'#0D2461', color:'#F5A800',
          opacity: submitting ? 0.5 : 1}}
          onClick={handleSubmitApproval} disabled={submitting}>
          {submitting ? 'Submitting…' : '📋 Submit Plan for Approval →'}
        </button>
      )}

      {project?.approval_status === 'awaiting_approval' && (
        <div style={{...callout, borderColor:'#c98a00', background:'#fff8e6', color:'#7a5c10'}}>
          ⏳ <strong>Awaiting approval.</strong> A manager or admin must approve this sampling plan before the sample can be drawn.
        </div>
      )}

      {project?.approval_status === 'approved' && (
        <button style={{...primaryBtn, opacity:(drawing||saving||(credits!==null&&credits<1))?0.5:1}}
          onClick={handleDraw} disabled={drawing||saving||(credits!==null&&credits<1)}>
          {drawing ? '🎲 Drawing…' : saving ? 'Saving…' : '🎲 Draw Random Sample →'}
        </button>
      )}
      </>)}
    </div>
  );
}

// ── Visual Strata Slider Component ───────────────────────────────────────────
function StrataSlider({ population, large100, strata, numStrata, onBoundariesChange }) {
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [handles, setHandles] = useState([]);
  const W = 760, H = 80, PAD = 20;

  const large100Ids = new Set((large100 || []).map(p => p._id));
  const plotPop = (population || []).filter(p => !large100Ids.has(p._id));
  const amounts = plotPop.map(p => p.amount).filter(a => a > 0);
  const minAmt = amounts.length > 0 ? Math.min(...amounts) : 0;
  const maxAmt = amounts.length > 0 ? Math.max(...amounts) : 1;

  // Log scale mapping — defined before hooks so useEffect can reference them
  const toX = (amt) => {
    if (maxAmt === minAmt) return PAD + (W - 2 * PAD) / 2;
    const logMin = Math.log10(Math.max(minAmt, 1));
    const logMax = Math.log10(Math.max(maxAmt, 1));
    const logVal = Math.log10(Math.max(amt, 1));
    return PAD + ((logVal - logMin) / (logMax - logMin)) * (W - 2 * PAD);
  };

  const fromX = (x) => {
    const logMin = Math.log10(Math.max(minAmt, 1));
    const logMax = Math.log10(Math.max(maxAmt, 1));
    const t = (x - PAD) / (W - 2 * PAD);
    return Math.pow(10, logMin + t * (logMax - logMin));
  };

  // All hooks must be called before any early return
  useEffect(() => {
    if (amounts.length === 0) return;
    if (strata && strata.length > 0) {
      const bounds = strata.slice(0, -1).map(st => st.boundary || st.projects[st.projects.length - 1]?.amount || 0).filter(b => b > 0);
      setHandles(bounds.map(b => toX(b)));
    }
  }, [strata.length, numStrata]); // eslint-disable-line

  const handleMouseDown = (e, idx) => {
    e.preventDefault();
    setDragging(idx);
  };

  const handleMouseMove = useCallback((e) => {
    if (dragging === null || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = W / rect.width;
    const x = Math.min(Math.max((e.clientX - rect.left) * scaleX, PAD + 5), W - PAD - 5);
    const newHandles = [...handles];
    newHandles[dragging] = x;
    newHandles.sort((a, b) => a - b);
    setHandles(newHandles);
    onBoundariesChange(newHandles.map(hx => fromX(hx)));
  }, [dragging, handles, onBoundariesChange]); // eslint-disable-line

  const handleMouseUp = useCallback(() => setDragging(null), []);

  // Early return AFTER all hooks
  if (amounts.length === 0) return null;

  // Color for a dot based on which stratum it falls in
  const getColor = (amt) => {
    for (let i = 0; i < handles.length; i++) {
      if (toX(amt) <= handles[i]) return STRATA_COLORS[i] || '#8a96b0';
    }
    return STRATA_COLORS[Math.min(handles.length, STRATA_COLORS.length - 1)] || '#8a96b0';
  };

  // Tick marks
  const ticks = [];
  const logMin = Math.log10(Math.max(minAmt, 1));
  const logMax = Math.log10(Math.max(maxAmt, 1));
  for (let exp = Math.floor(logMin); exp <= Math.ceil(logMax); exp++) {
    const val = Math.pow(10, exp);
    if (val >= minAmt && val <= maxAmt) {
      ticks.push({ x: toX(val), label: val >= 1000000 ? `$${(val/1000000).toFixed(0)}M` : val >= 1000 ? `$${(val/1000).toFixed(0)}K` : `$${val}` });
    }
  }

  return (
    <div style={{ userSelect: 'none' }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}>
      <div style={{ fontFamily:"'Roboto Mono',monospace", fontSize:10, color:'#8a96b0',
        letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:8 }}>
        Drag handles to adjust strata boundaries · {plotPop.length} projects shown
      </div>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H + 30}`} style={{ width:'100%', display:'block', cursor: dragging !== null ? 'grabbing' : 'default' }}>
        {/* Axis line */}
        <line x1={PAD} y1={H/2} x2={W-PAD} y2={H/2} stroke={BORDER} strokeWidth={1} />

        {/* Strata color bands */}
        {[...handles, W - PAD].map((hx, i) => {
          const prevX = i === 0 ? PAD : handles[i - 1];
          return <rect key={i} x={prevX} y={H/2 - 12} width={hx - prevX} height={24}
            fill={STRATA_COLORS[i] || '#8a96b0'} opacity={0.08} />;
        })}

        {/* Dots */}
        {plotPop.map((p, i) => (
          <circle key={i} cx={toX(p.amount)} cy={H/2 + (Math.sin(i * 2.3) * 14)}
            r={3} fill={getColor(p.amount)} opacity={0.7} />
        ))}

        {/* Large items shown differently */}
        {(large100 || []).map((p, i) => (
          <circle key={`l${i}`} cx={toX(p.amount)} cy={H/2}
            r={5} fill="none" stroke="#c03a1a" strokeWidth={2} opacity={0.8} />
        ))}

        {/* Drag handles */}
        {handles.map((hx, i) => (
          <g key={i} style={{ cursor: 'ew-resize' }} onMouseDown={(e) => handleMouseDown(e, i)}>
            <line x1={hx} y1={H/2 - 20} x2={hx} y2={H/2 + 20} stroke={NAVY} strokeWidth={2} />
            <circle cx={hx} cy={H/2 - 22} r={6} fill={NAVY} />
            <text x={hx} y={H/2 - 20} textAnchor="middle" fill="white"
              fontSize={7} fontFamily="monospace" dy={2}>{i+1}</text>
            <rect x={hx - 20} y={H/2 + 22} width={40} height={14} rx={3} fill={NAVY} opacity={0.9} />
            <text x={hx} y={H/2 + 31} textAnchor="middle" fill={AMBER}
              fontSize={8} fontFamily="monospace">{fmt$(Math.round(fromX(hx)))}</text>
          </g>
        ))}

        {/* Tick marks */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={t.x} y1={H/2 + 4} x2={t.x} y2={H/2 + 10} stroke="#8a96b0" strokeWidth={1} />
            <text x={t.x} y={H + 22} textAnchor="middle" fontSize={9}
              fontFamily="monospace" fill="#8a96b0">{t.label}</text>
          </g>
        ))}

        {/* Legend */}
        <circle cx={W - 100} cy={H/2} r={5} fill="none" stroke="#c03a1a" strokeWidth={2} />
        <text x={W - 90} y={H/2 + 4} fontSize={9} fontFamily="monospace" fill="#8a96b0">= certainty</text>
      </svg>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const card       = { background:'white', border:`1.5px solid ${BORDER}`, borderRadius:8, padding:40 };
const eyebrow    = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:AMBER, marginBottom:6 };
const title      = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:24, color:NAVY, margin:'0 0 10px' };
const sub        = { fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#5a6a8a', lineHeight:1.7, marginBottom:24 };
const placeholder= { background:CREAM, border:`1.5px dashed ${BORDER}`, borderRadius:6, padding:40, textAlign:'center', fontFamily:"'Roboto Mono',monospace", fontSize:13, color:'#8a96b0' };
const callout    = { background:'#e8ecf5', borderLeft:`3px solid ${NAVY}`, borderRadius:'0 6px 6px 0', padding:'14px 18px', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, lineHeight:1.6 };
const errorBox   = { background:'#fff0ee', border:'1px solid #e05a3a', borderRadius:6, padding:'10px 14px', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#c03a1a', marginBottom:16 };
const controlBox = { background:CREAM, border:`1.5px solid ${BORDER}`, borderRadius:6, padding:'20px 24px', marginBottom:20 };
const controlTitle={ fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'#5a6a8a', marginBottom:16 };
const twoCol     = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 };
const fieldGroup = { display:'flex', flexDirection:'column', gap:6 };
const label      = { fontFamily:"'Roboto Mono',monospace", fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', color:'#5a6a8a' };
const hint       = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', lineHeight:1.6 };
const inlineRow  = { display:'flex', alignItems:'center', gap:10 };
const numInput   = { padding:'8px 10px', border:`1.5px solid ${BORDER}`, borderRadius:4, fontFamily:"'Roboto Mono',monospace", fontSize:14, fontWeight:600, textAlign:'center', color:NAVY, outline:'none', background:'white' };
const toggleBtn  = { padding:'8px 16px', border:'1.5px solid', borderRadius:4, fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:13, cursor:'pointer' };
const modeTabs   = { display:'flex', gap:0, border:`1.5px solid ${BORDER}`, borderRadius:4, overflow:'hidden', marginBottom:16 };
const modeTab    = { flex:1, padding:'9px 8px', background:'white', border:'none', borderRight:`1.5px solid ${BORDER}`, fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.05em', textTransform:'uppercase', color:'#8a96b0', cursor:'pointer' };
const modeTabActive = { background:NAVY, color:AMBER, fontWeight:700 };
const pill       = { flex:1, padding:'9px 0', border:`1.5px solid ${BORDER}`, borderRadius:4, background:'white', fontFamily:"'Roboto Mono',monospace", fontSize:12, fontWeight:600, color:'#8a96b0', cursor:'pointer', textAlign:'center' };
const pillActive = { background:NAVY, borderColor:NAVY, color:AMBER };
const statRow    = { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 };
const stat       = { background:CREAM, border:`1.5px solid ${BORDER}`, borderRadius:6, padding:'14px 16px' };
const statLabel  = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.08em', textTransform:'uppercase', color:'#8a96b0', marginBottom:4 };
const statVal    = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:22, color:NAVY };
const table      = { width:'100%', borderCollapse:'collapse', fontFamily:"'Roboto Mono',monospace", fontSize:12 };
const th         = { background:NAVY, color:AMBER, padding:'8px 12px', textAlign:'left', fontSize:10, letterSpacing:'0.06em', textTransform:'uppercase' };
const td         = { padding:'9px 12px', color:'#5a6a8a' };
const strataBadge= { borderRadius:3, padding:'2px 8px', fontFamily:"'Roboto Mono',monospace", fontSize:10, fontWeight:600 };
const primaryBtn = { padding:'13px 28px', background:AMBER, color:NAVY, border:'none', borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:14, cursor:'pointer' };
